import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { API } from '../../apis/api';
import { buildErrorMessage } from '../../apis/calls';
import {
  AggregatePlan,
  AggregateConflict,
  TabbedViewWithDateSelect,
} from '../../components';
import { getSequenceStartDateString } from '../../utils/tableUtils';
import { getDatesForRange, getMonthNameWithYear, shouldIncludeColourAndCountry } from '../../utils/utils';
import {API_RESOURCE_URLS, DATE_FORMAT } from '../../constants';
import { useAjaxCallbackWithIntervals } from '../../hooks';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';

const AggregatePlanView = ({ aggregate }) => {
  const { showInternalError, showError } = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;

  const [sequenceGenerationDate, setSequenceGenerationDate] = useState();
  const [freezableDate] = useState(null);

  const [rowsDataForPlan, setRowsDataForPlan] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [planCount, setPlanCount] = useState(0);
  const handlePlanCountChange = count => {
    setPlanCount(count);
  };
  const [datesForAMonth, setDatesForAMonth] = useState([]);

  const [rowsDataForConflict, setRowsDataForConflict] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });
  const [conflictCount, setConflictCount] = useState(0);
  const [colours, setColours] = useState({ loading: false, data: [] });
  const [countries, setCountries] = useState({ loading: false, data: [] });

  const includeColoursAndCountries = shouldIncludeColourAndCountry(aggregate);

  const handleConflictCountChange = count => {
    const resolvedConflictData = rowsDataForConflict?.filteredData?.filter(
      record => record.unAllocatedQty === 0,
    );
    if (resolvedConflictData?.length) {
      setConflictCount(count - resolvedConflictData?.length);
    } else {
      setConflictCount(count);
    }
  };

  const title = 'Aggregate Plan';

  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const [areColumnsEditable, setEditableColumns] = useState(false);

  const [startDate, setStartDate] = useState();
  const [isGeneratedEnabled,setIsGeneratedEnabled]=useState(false);
  const[isFetchingLastAgsnDate,setIsFetchingLastAgsnDate]=useState(false);

  const [activeMonthNameWithYear, setActiveMonthNameWithYear] = useState(
    getMonthNameWithYear(moment()),
  );

  const aggregateShopName = aggregate;
  const fetchAggregateConflictData = async () => {
    const response = await API.get(
      API_RESOURCE_URLS.getAggregatePlanConflicts(
        plant.value,
        aggregateShopName,
        activeAsnDate.format(DATE_FORMAT.ISO),
      ),
    );

    const { data } = response;
    const conflictData = data.filter(
      record =>
        record.lineAndAllocatedProducedChildMaterialsQuantities.length > 1,
    );
    return conflictData;
  };

  const loadAggregateConflicts = async () => {
    try {
      if (
        plant.value &&
        aggregateShopName &&
        aggregateLine.value &&
        activeAsnDate
      ) {
        setRowsDataForConflict(prevState => ({ ...prevState, loading: true }));
        const data = await fetchAggregateConflictData();
        const resolvedConflictData = data?.map(item => {
          let sumOfChildQty = 0;
          item.lineAndAllocatedProducedChildMaterialsQuantities.forEach(
            childItem => {
              sumOfChildQty += childItem.allocatedProducedMaterialsQuantity;
            },
          );
          item['unAllocatedQty'] =
            item.totalProducedChildMaterialsQuantity - sumOfChildQty;
          return item;
        });
        if (resolvedConflictData?.length) {
          setConflictCount(data.length - resolvedConflictData?.length);
        } else {
          setConflictCount(data.length);
        }
        setRowsDataForConflict({
          loading: false,
          data,
          filteredData: [...data],
        });
      }
    } catch (err) {
      console.error(buildErrorMessage(err));
      setRowsDataForConflict(prevState => ({
        ...prevState,
        data: [],
        loading: false,
      }));
      showInternalError();
    }
  };

  const getStartDate = date => {
    if (date) return moment(date);
    return moment();
  };

  const getAggregatePlanDataOfPlantAndLineForDates = async (
    plantValue,
    lineValue,
    startDate,
    endDate,
  ) => {
    const response = await API.get(
      API_RESOURCE_URLS.aggregatePlan(
        plantValue,
        lineValue,
        startDate,
        endDate,
      ),
    );
    return response.data;
  };

  const loadAggregatePlanForDateRange = async (startDate, endDate) => {
    try {
      if (plant.value && aggregateLine.value) {
        setRowsDataForPlan(prevState => ({ ...prevState, loading: true }));
        const data = await getAggregatePlanDataOfPlantAndLineForDates(
          plant.value,
          aggregateLine.value,
          startDate,
          endDate,
        );

        const dates = getDatesForRange(startDate, endDate);
        const processedData = processAggregatePlanData(data, dates);
        setDatesForAMonth(dates);
        handlePlanCountChange(processedData.length);
        setRowsDataForPlan({
          loading: false,
          data: processedData,
          filteredData: processedData,
        });
      }
    } catch (error) {
      setRowsDataForPlan(prevState => ({
        ...prevState,
        data: [],
        loading: false,
      }));
      console.error('Failed to load Plan.' + buildErrorMessage(error));
      showError({ contextText: 'Failed to load Plan', error });
    }
  };

  const processAggregatePlanData = (responseData, dates) => {
  const uniqueMaterials = [];

  responseData.forEach(data => {
    const isItemPresentInUniqueRecords = uniqueMaterials.find(
      item =>
        item.materialNumber === data.materialNumber &&
        item.destination === data.destination &&
        item.colourCode === data.colourCode &&
        item.countryCode === data.countryCode
    );
    if (!isItemPresentInUniqueRecords) {
      const materialWithQuantities = responseData.reduce(
        (acc, value) => {
          if (
            value.materialNumber === data.materialNumber &&
            value.destination === data.destination &&
            value.colourCode === data.colourCode &&
            value.countryCode === data.countryCode
          ) {
            return {
              ...value,
              plannedQuantity: acc.plannedQuantity + value.plannedQuantity,
              producedQuantity: acc.producedQuantity + value.producedQuantity,
              plan: {
                ...acc.plan,
                [moment(value.date).format(DATE_FORMAT.ISO)]: {
                  quantity: value.plannedQuantity,
                },
              },
            };
          }
          return acc;
        },
        { plannedQuantity: 0, producedQuantity: 0, plan: {} },
      );
      uniqueMaterials.push(materialWithQuantities);
    }
  });

  const materialsWithZeroQuantities = uniqueMaterials
    .map(uniqueMaterial => ({
      ...uniqueMaterial,
      ...dates.reduce(
        (acc, date) => {
          if (!uniqueMaterial.plan[moment(date).format(DATE_FORMAT.ISO)]) {
            return {
              ...acc,
              plan: {
                ...acc.plan,
                [moment(date).format(DATE_FORMAT.ISO)]: { quantity: 0 },
              },
            };
          }
          return { ...acc, plan: { ...acc.plan, ...uniqueMaterial.plan } };
        },
        { plan: {} },
      ),
    }))
    .map(item => ({
      ...item,
      ...item.plan,
      target: item.plannedQuantity,
      balance: item.plannedQuantity - item.producedQuantity,
    }));

  return materialsWithZeroQuantities;
};

  const loadAggregatePlanForAMonth = async () => {
    const selectedMonthNameWithYear = activeMonthNameWithYear.split('-'); // activeMonthNameWithYear will be in form Jan-2021

    const selectedMonthName = selectedMonthNameWithYear[0];
    const yearOfSelectedMonth = selectedMonthNameWithYear[1];

    const startDate = moment()
      .month(selectedMonthName)
      .year(yearOfSelectedMonth)
      .startOf('month');
    const startDateString = startDate.format(DATE_FORMAT.ISO);

    const endDate = moment()
      .month(selectedMonthName)
      .year(yearOfSelectedMonth)
      .endOf('month');
    const endDateString = endDate.format(DATE_FORMAT.ISO);
    return loadAggregatePlanForDateRange(startDateString, endDateString);
  };

  const fetchLastAgSNGeneratedDate = async (plantId, lineId) => {
    try {
      setIsFetchingLastAgsnDate(true)
      const response = await API.get(
        API_RESOURCE_URLS.lastAgSNGeneratedDate(plantId, lineId),
      );
      const { data } = response;
      const lastAgsnDate = data.date ? moment(data.date) : null;
      const sequenceDate = getSequenceStartDateString(lastAgsnDate);
      setSequenceGenerationDate(sequenceDate);
      setIsGeneratedEnabled(!!data.date)
    } catch (error) {
      console.error(buildErrorMessage(error));
      setIsGeneratedEnabled(false)
      showError({
        contextText: `Failed to Fetch Last AGSN Date`,
        error: error,
      });
    }finally{
      setIsFetchingLastAgsnDate(false);
    }
  };

  const handleReloadData = async () => {
    if (plant.value && aggregateLine.value) {
      fetchLastAgSNGeneratedDate(plant.value, aggregateLine.value);
      loadAggregatePlanForAMonth();
      await loadAggregateConflicts();
    }
  };

  const loadColours = async () => {
    try {
      const response = await API.get(API_RESOURCE_URLS.COLOURS);
      setColours({ loading: false, data: response.data });
    } catch (error) {
      showError({
        contextText: `Failed to Load colours`,
        error: error,
      });
    }
  };

  const loadCountries = async () => {
    try {
      const response = await API.get(API_RESOURCE_URLS.COUNTRIES);
      setCountries({ loading: false, data: response.data });
    } catch (error) {
      showError({
        contextText: `Failed to Load colours`,
        error: error,
      });
    }
  };

  const colourOptions = includeColoursAndCountries
    ? colours.data?.map(({ colourCode, colourDescription }) => ({
        value: colourDescription + " (" + colourCode + ")",
        label: colourDescription + " (" + colourCode + ")",
      })) || []
    : [];

  const countryOptions = includeColoursAndCountries
    ? countries.data?.map(({ code }) => ({
        value: code,
        label: code,
      })) || []
    : [];
    
  const lastRefreshedDateForPlan = useAjaxCallbackWithIntervals(
    rowsDataForPlan.loading,
    false,
    loadAggregatePlanForAMonth,
    aggregate,
    plant.value,
    aggregateLine.value,
    activeMonthNameWithYear,
  );

  const lastRefreshedDateForConflict = useAjaxCallbackWithIntervals(
    rowsDataForConflict.loading,
    areColumnsEditable,
    loadAggregateConflicts,
    activeAsnDate,
    areColumnsEditable,
  );

  useEffect(() => {
    if (includeColoursAndCountries) {
      loadColours();
      loadCountries();
    } else {
      setColours({ loading: false, data: [] });
      setCountries({ loading: false, data: [] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregateLine.label]);
  useEffect(() => {
    setEditableColumns(false);
  }, [activeAsnDate]);

  useEffect(() => {
    if (plant.value && aggregateLine.value) {
      fetchLastAgSNGeneratedDate(plant.value, aggregateLine.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, aggregateLine]);

  useEffect(() => {
    const startDate = getStartDate(freezableDate);
    setStartDate(startDate);
    setActiveAsnDate(startDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezableDate]);

  return (
    <TabbedViewWithDateSelect
      tabs={[
        {
          label: 'Plan',
          title: 'Plan',
          count: planCount,
          component: AggregatePlan,
          props: {
            aggregate,
            lastRefreshedDate: lastRefreshedDateForPlan,
            rowsData: rowsDataForPlan,
            dates: datesForAMonth,
            setRowsData: setRowsDataForPlan,
            setCount: handlePlanCountChange,
            sequenceGenerationDate,
            freezableDate,
            activeMonthNameWithYear,
            setActiveMonthNameWithYear,
            loadAggregatePlanForAMonth,
            colourOptions,
            countryOptions,
            isGeneratedEnabled,
            isFetchingLastAgsnDate,
          },
        },
        {
          label: 'Conflict',
          title: 'Conflict',
          count: conflictCount,
          countColor: 'red',
          component: AggregateConflict,
          props: {
            aggregate,
            lastRefreshedDate: lastRefreshedDateForConflict,
            loadAggregateConflicts,
            areColumnsEditable,
            setEditableColumns,
            rowsData: rowsDataForConflict,
            setConflictCount: handleConflictCountChange,
            setRowsData: setRowsDataForConflict,
            startDate,
            activeAsnDate,
            setActiveAsnDate,
          },
        },
      ]}
      pageTitle={title}
      reloadData={handleReloadData}
    />
  );
};

export default AggregatePlanView;
